/**
 * RestoreCodeEntry.tsx — the Option-C email-code gate for a RETURN visit.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: trust-model re-entry (Option C).
 *
 * WHY: a bare assessment UUID in a URL/log must not unlock a patient's
 * health/PII or an account. So before any sensitive restore read
 * (result-summary / account-bootstrap) or account creation on a RETURN visit,
 * the visitor proves control of the assessment's email with a 6-digit code. On
 * mount we ask the server to email one (generic 200 — no oracle); the visitor
 * types it back; a correct code mints a short-lived re-entry TOKEN which the
 * caller then carries in state and posts in the body of the sensitive reads.
 *
 * FAIL-SOFT, like the rest of the journey: a request/verify fault never dead-ends
 * — the visitor can resend or retry. The IN-FLOW ResultScreen (same session,
 * right after submit) does NOT reach here; this is only for a cold refresh /
 * return link where the journey state is gone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { requestRestoreCode, verifyRestoreCode } from '../api/journey';
import { Alert, Button, Card, Field, Help, TextInput, Title } from '../ui/kit';

const CODE_LENGTH = 6;
/** The optional delivery-help line under the code box (see `deliveryHelp`). */
export const RESTORE_CODE_HELP_ID = 'restore-code-help';

export function RestoreCodeEntry({
  assessmentId,
  onVerified,
  heading = 'Confirm it’s you',
  intro = 'To pick up where you left off we’ve emailed a 6-digit code to the address on your check. Enter it below to continue.',
  skipInitialSend = false,
  deliveryHelp,
}: {
  assessmentId: string;
  /** Called with the freshly-minted re-entry token on the first correct code. */
  onVerified: (reEntryToken: string) => void;
  heading?: string;
  intro?: string;
  /**
   * Optional "can't find it?" line rendered directly UNDER the code box, above
   * the Continue / Resend controls, and named by the box's `aria-describedby`
   * so a screen-reader user hears it with the field. The checkout account step
   * passes one (CTO 2026-09-24: check spam/junk, it can take a minute). Absent →
   * nothing renders and the box's description is exactly what it was.
   */
  deliveryHelp?: string;
  /**
   * Skip the on-mount code request. Set when the caller already pre-sent the
   * code (the in-flow checkout account step sends it silently on page load, so
   * it's waiting by the time this gate reveals) — avoids a duplicate email and
   * a fresh code that would invalidate the one the visitor already has.
   */
  skipInitialSend?: boolean;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  // Send the first code exactly once on mount (StrictMode double-invoke guard).
  const requested = useRef(false);

  const sendCode = useCallback(() => {
    // Generic by design — a request never reveals whether the email exists, so we
    // never surface a failure here; the visitor simply types the code they got.
    void requestRestoreCode(assessmentId).catch(() => undefined);
  }, [assessmentId]);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    if (skipInitialSend) return;
    sendCode();
  }, [sendCode, skipInitialSend]);

  const canVerify = code.trim().length === CODE_LENGTH && !busy;

  async function handleVerify(e?: { preventDefault: () => void }) {
    e?.preventDefault();
    if (!canVerify) return;
    setBusy(true);
    setError(null);
    try {
      const { reEntryToken } = await verifyRestoreCode(assessmentId, code.trim());
      onVerified(reEntryToken);
    } catch {
      // Wrong / expired / attempt-capped — one neutral message (no oracle).
      setError('That code didn’t match. Check it, or resend a new one below.');
    } finally {
      setBusy(false);
    }
  }

  function handleResend() {
    setError(null);
    setCode('');
    setResent(true);
    sendCode();
  }

  return (
    <Card>
      <Title>{heading}</Title>
      <Help>{intro}</Help>
      {/* A real <form> so Enter submits the code (not just the button click), and
          the error is tied to the input via aria-describedby for screen readers. */}
      <form onSubmit={(e) => void handleVerify(e)} noValidate>
        {error ? (
          <Alert id="restore-code-error" tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {error}
          </Alert>
        ) : null}
        {resent && !error ? (
          <Alert tone="success" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
            If that email is on file, a new code is on its way.
          </Alert>
        ) : null}
        <Field label="6-digit code" htmlFor="restore-code">
          <TextInput
            id="restore-code"
            value={code}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            placeholder="000000"
            aria-describedby={
              [error ? 'restore-code-error' : null, deliveryHelp ? RESTORE_CODE_HELP_ID : null]
                .filter(Boolean)
                .join(' ') || undefined
            }
            aria-invalid={error ? true : undefined}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
          />
        </Field>
        {deliveryHelp ? (
          <Help id={RESTORE_CODE_HELP_ID} style={{ fontSize: '14.5px', margin: '0 0 var(--gps-s-2)' }}>
            {deliveryHelp}
          </Help>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--gps-s-2)', marginTop: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
          <Button type="submit" variant="cta" disabled={!canVerify}>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
          <Button type="button" variant="ghost" disabled={busy} onClick={handleResend}>
            Resend code
          </Button>
        </div>
      </form>
    </Card>
  );
}
